import { transformExtent, fromLonLat } from 'ol/proj';
import { Point, Polygon } from 'ol/geom';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import Feature from 'ol/Feature';
import { register } from 'ol/proj/proj4';
import {
  Fill, Icon, Stroke, Style,
} from 'ol/style';
import proj4 from 'proj4';
import TileGrid from 'ol/tilegrid/TileGrid';
import { Tile, Vector } from 'ol/layer';
import View from 'ol/View';
import XYZ from 'ol/source/XYZ';
import { Map } from 'ol';
import { defaults as defaultControls } from 'ol/control';

const apiKey = import.meta.env.VITE_OS_MAPS_API_KEY;
const tileServiceUrl = 'https://api.os.uk/maps/raster/v1/zxy/';
const projectionName = 'EPSG:27700';

let polygonLayer;
let markerLayer;

function getCentreCoords() {
  const coords = JSON.parse(document.querySelector('#title-plan-data').dataset.center);
  return [coords.lng, coords.lat];
}

// Marker layer
function getMarker(map) {
  markerLayer = new Vector({
    visible: false,
    source: new VectorSource(),
    style: new Style({
      image: new Icon({
        anchor: [0.5, 1],
        scale: 0.05,
        src: '/img/map-marker.svg',
      }),
    }),
  });
  map.addLayer(markerLayer);

  const marker = new Feature(new Point(fromLonLat(getCentreCoords(), projectionName)));
  markerLayer.getSource().addFeature(marker);
}

function getPolygonCoordinates() {
  return JSON.parse(document.querySelector('#title-plan-data').dataset.polygonCoordinates);
}

function defineProjection() {
  proj4.defs(projectionName, '+proj=tmerc +lat_0=49 +lon_0=-2 +k=0.9996012717 +x_0=400000 +y_0=-100000 +ellps=airy +towgs84=446.448,-125.157,542.06,0.15,0.247,0.842,-20.489 +units=m +no_defs');
  register(proj4);
}

function togglePolygons() {
  polygonLayer.setVisible(!polygonLayer.getVisible());
}

function toggleMarker(map) {
  if (!markerLayer) {
    getMarker(map);
  }

  if (markerLayer.getVisible() === true) {
    markerLayer.setVisible(false);
  } else {
    markerLayer.setVisible(true);
  }
}

function getBoundary(map, style) {
  const polygonCoordinates = getPolygonCoordinates();

  polygonCoordinates.forEach((coordinates) => {
    const boundaryPolygon = new Feature(new Polygon([coordinates]));

    polygonLayer = new VectorLayer({
      source: new VectorSource({ features: [boundaryPolygon] }),
      style,
    });
    map.addLayer(polygonLayer);
  });
}

// eslint-disable-next-line func-names
export default async function () {
  if (
    (window.location.pathname.slice(-4) !== 'edit' && document.getElementById('titlePlan')?.src !== undefined)
    || !document.querySelector('#title-plan-data')?.dataset
  ) {
    return;
  }

  defineProjection();

  const tilegrid = new TileGrid({
    resolutions: [896.0, 448.0, 224.0, 112.0, 56.0, 28.0, 14.0, 7.0, 3.5, 1.75, 0.875, 0.4375, 0.21875, 0.109375],
    origin: [-238375.0, 1376256.0],
  });

  const map = new Map({
    layers: [
      new Tile({
        source: new XYZ({
          url: `${tileServiceUrl}Road_27700/{z}/{x}/{y}.png?key=${apiKey}`,
          projection: projectionName,
          tileGrid: tilegrid,
          crossOrigin: 'anonymous',
          attributions: `Contains OS data &copy; Crown copyright ${new Date().getFullYear()}`,
          attributionsCollapsible: false,
        }),
      }),
    ],
    controls: defaultControls({
      rotate: false,
      zoom: (window.location.pathname).split('/').includes('edit'),
    }),
    target: 'titlePlan',
    view: new View({
      projection: projectionName,
      extent: transformExtent([-10.76418, 49.528423, 1.9134116, 61.331151], 'EPSG:4326', projectionName),
      minZoom: 11,
      maxZoom: 13,
      resolutions: tilegrid.getResolutions(),
      center: fromLonLat(getCentreCoords(), projectionName),
      zoom: 11.5,
    }),
  });

  // Listen for map overlay toggle button clicks
  document.getElementById('toggle-polygons')?.addEventListener('click', () => {
    togglePolygons(map);
  });

  document.getElementById('toggle-marker')?.addEventListener('click', () => {
    toggleMarker(map);
  });

  // Set stroke and fill of polygons
  const style = new Style({
    fill: new Fill({
      color: 'rgba(5, 136, 255, 0.4)',
    }),
    stroke: new Stroke({
      width: 2,
      color: '#2147F4',
    }),
  });

  if (getPolygonCoordinates()) {
    getBoundary(map, style);
  }

  // Add class to element once map has finished loading
  map.once('rendercomplete', () => {
    document.getElementById('titlePlan').classList.add('title-plan-loaded');
  });
}
